import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";
import { Sparkle, PaperPlaneTilt, Copy, Check } from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Tap-to-run prompts pinned above the input. Kept short so the chips fit on
// mobile without wrapping onto three lines.
const STARTER_PROMPTS = [
  { id: "week", label: "Summarise this week" },
  { id: "followups", label: "What follow-ups are open?" },
  { id: "opps", label: "Any open opportunities?" },
];

export default function Assistant() {
  const [sid] = useState(() => localStorage.getItem("al_chat_sid") || (() => { const s = crypto.randomUUID(); localStorage.setItem("al_chat_sid", s); return s; })());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const endRef = useRef();
  const abortRef = useRef();
  const bubbleRefs = useRef({});
  const autoRanRef = useRef(false);

  // Copy an assistant reply to the clipboard. Writes BOTH HTML and plain
  // markdown so rich-text editors (Google Docs, Gmail) pick up formatting
  // while markdown-native editors (Obsidian, Notion source) still get useful
  // text. Falls back to plain writeText on browsers without ClipboardItem.
  const copyReply = useCallback(async (m) => {
    const el = bubbleRefs.current[m.id];
    const plain = m.text || "";
    const html = el ? el.innerHTML : plain;
    try {
      if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plain);
      }
      setCopiedId(m.id);
      setTimeout(() => setCopiedId((c) => (c === m.id ? null : c)), 1600);
    } catch {
      // Some browsers block clipboard access outside of a user gesture — try
      // a last-ditch execCommand for the plain-text version so the user still
      // gets something.
      try {
        const ta = document.createElement("textarea");
        ta.value = plain;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopiedId(m.id);
        setTimeout(() => setCopiedId((c) => (c === m.id ? null : c)), 1600);
      } catch { /* give up silently — the bubble text is still readable */ }
    }
  }, []);

  const loadHistory = useCallback(async () => {
    const { data } = await api.get(`/ai/history/${sid}`);
    // Only prime the message list from the server when the local list is
    // still empty. If a Dashboard `?ask=` prompt has already appended
    // optimistic bubbles we must NOT clobber them.
    setMessages((prev) => {
      if (prev.length > 0) return prev;
      return data.flatMap((m) => [
        { id: `${m.id}-u`, role: "user", text: m.message },
        { id: `${m.id}-a`, role: "assistant", text: m.reply },
      ]);
    });
    setHistoryLoaded(true);
  }, [sid]);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Consume the SSE stream from /api/ai/assistant/stream. Uses fetch (not axios,
  // which buffers the whole body) with credentials so the auth cookie rides
  // along in production. Each `data: {text}` frame appends to the last
  // assistant message so long replies visibly type out live.
  const streamReply = useCallback(async (msg, assistantId) => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const resp = await fetch(`${API_BASE}/api/ai/assistant/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      signal: ctrl.signal,
      body: JSON.stringify({ session_id: sid, message: msg }),
    });
    if (!resp.ok || !resp.body) throw new Error(`stream ${resp.status}`);
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    // Loop reading chunks. SSE frames are separated by a blank line ("\n\n").
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const line = frame.split("\n").find((l) => l.startsWith("data:"));
        if (!line) continue;
        try {
          const evt = JSON.parse(line.slice(5).trim());
          if (evt.text) {
            setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, text: m.text + evt.text } : m)));
          } else if (evt.done) {
            return;
          } else if (evt.error) {
            throw new Error(evt.error);
          }
        } catch { /* ignore malformed keep-alive line */ }
      }
    }
  }, [sid]);

  const runPrompt = useCallback(async (msg) => {
    if (!msg || busy) return;
    const userId = crypto.randomUUID();
    const assistantId = crypto.randomUUID();
    setMessages((m) => [
      ...m,
      { id: userId, role: "user", text: msg },
      { id: assistantId, role: "assistant", text: "" },
    ]);
    setInput("");
    setBusy(true);
    try {
      await streamReply(msg, assistantId);
    } catch {
      setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, text: m.text || "Sorry, I couldn't answer just now." } : m)));
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  }, [busy, streamReply]);

  const send = (e) => {
    e.preventDefault();
    runPrompt(input.trim());
  };

  // Abort any in-flight stream on unmount so a page navigation doesn't leak
  // an open connection. Guard the ref so React 18 StrictMode's double-mount
  // (dev-only) doesn't cancel a stream that will still receive tokens on the
  // second mount.
  useEffect(() => () => {
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
  }, []);

  // If the Dashboard's "Ask AL-AXE" bar routed here with ?ask=..., fire the
  // prompt automatically ONCE, but only after the initial history fetch has
  // resolved. Otherwise loadHistory could race with the optimistic bubbles
  // appended by runPrompt and wipe them.
  useEffect(() => {
    if (!historyLoaded) return;
    const prefill = searchParams.get("ask");
    if (!prefill || autoRanRef.current) return;
    autoRanRef.current = true;
    runPrompt(prefill);
    const next = new URLSearchParams(searchParams);
    next.delete("ask");
    setSearchParams(next, { replace: true });
  }, [historyLoaded, searchParams, setSearchParams, runPrompt]);

  return (
    <div data-testid="assistant-page" className="flex flex-col overflow-hidden" style={{ height: "calc(100dvh - 10rem)" }}>
      <header className="mb-3 md:mb-5 shrink-0">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Source-linked AI</div>
        <h1 className="text-2xl md:text-4xl font-semibold tracking-tight flex items-center gap-2 md:gap-3"><Sparkle className="al-gradient-text" weight="fill"/> AL-AXE Assistant</h1>
        <p className="hidden md:block text-sm text-slate-500 mt-1">Ask questions grounded in your operations journal.</p>
      </header>
      <div className="flex-1 overflow-y-auto scrollbar-thin space-y-3 md:pr-2 min-h-[120px]">
        {messages.length === 0 && (
          <div data-testid="assistant-empty" className="text-center text-slate-400 py-6 md:py-16">
            <div className="text-sm">Ask anything — or tap a starter below.</div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`group max-w-2xl rounded-2xl px-4 py-3 text-sm relative ${m.role === "user" ? "al-gradient text-white" : "bg-white border border-slate-200/70 text-slate-800 pt-9"}`}>
              {m.role === "user" ? (
                <div className="whitespace-pre-wrap">{m.text}</div>
              ) : (
                <>
                  <div
                    data-testid={`assistant-bubble-${m.id}`}
                    ref={(el) => { if (el) bubbleRefs.current[m.id] = el; else delete bubbleRefs.current[m.id]; }}
                    className="assistant-md whitespace-pre-wrap"
                  >
                    {m.text && (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          a: ({ node, ...props }) => (
                            <a {...props} target="_blank" rel="noreferrer" className="text-teal-600 hover:underline" />
                          ),
                        }}
                      >
                        {m.text}
                      </ReactMarkdown>
                    )}
                    {!m.text && busy && (
                      <span data-testid="assistant-streaming-indicator" className="inline-block w-2 h-4 bg-slate-300 rounded-sm animate-pulse align-middle" />
                    )}
                  </div>
                  {m.text && (
                    <button
                      type="button"
                      data-testid={`copy-reply-${m.id}`}
                      onClick={() => copyReply(m)}
                      title={copiedId === m.id ? "Copied" : "Copy reply"}
                      aria-label="Copy reply"
                      className={`absolute top-2 right-2 inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-slate-200 bg-white/95 backdrop-blur-sm text-slate-500 hover:text-teal-700 hover:border-teal-300 shadow-sm transition-colors ${copiedId === m.id ? "text-teal-700 border-teal-300" : ""}`}
                    >
                      {copiedId === m.id ? (
                        <><Check size={12} weight="bold" /> Copied</>
                      ) : (
                        <><Copy size={12} /> Copy</>
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
        {busy && messages[messages.length - 1]?.role === "assistant" && !messages[messages.length - 1]?.text && (
          <div className="text-xs text-slate-400 animate-pulse">AL-AXE is thinking…</div>
        )}
        <div ref={endRef} />
      </div>
      <div data-testid="assistant-starters" className="mt-3 md:mt-4 flex flex-wrap gap-2 shrink-0">
        {STARTER_PROMPTS.map((p) => (
          <button
            key={p.id}
            type="button"
            data-testid={`assistant-starter-${p.id}`}
            onClick={() => runPrompt(p.label)}
            disabled={busy}
            className="text-xs font-medium px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-teal-300 hover:text-teal-700 hover:shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-700"
          >
            {p.label}
          </button>
        ))}
      </div>
      <form onSubmit={send} className="mt-3 flex gap-2 relative shrink-0">
        <input data-testid="assistant-input" value={input} onChange={e=>setInput(e.target.value)} placeholder="Ask about your journal…" className="flex-1 rounded-full border border-slate-200 px-5 py-3 focus:outline-none focus:ring-2 focus:ring-teal-400"/>
        <button data-testid="assistant-send" disabled={busy} className="al-gradient text-white rounded-full px-5 py-3 disabled:opacity-60"><PaperPlaneTilt size={18} weight="fill"/></button>
      </form>
    </div>
  );
}
